import {
  motivoDeTransicaoSchema,
  ROTULO_DO_STATUS_DE_RESERVA,
  rotuloDoNivelDeCombustivel,
  type TrocarVeiculo,
  trocarVeiculoSchema,
  type VistoriaAvulsa,
  vistoriaAvulsaSchema,
} from '@locaveri/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, ArrowRightLeft, Check, LoaderCircle } from 'lucide-react'
import { type ReactElement, useRef, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { Campo } from '@/components/form/campo'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { NotFoundState } from '@/components/state/not-found-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { type CandidatoLido, useCandidatos, useReserva } from '@/features/reservas/api'
import {
  corpoDaVistoria,
  distribuirErrosDaVistoria,
  type ErrosDaVistoria,
  FormularioDeVistoria,
  type FormularioDeVistoriaHandle,
  VALOR_INICIAL_DA_VISTORIA,
  type ValorDaVistoria,
} from '@/features/vistorias/formulario-de-vistoria'
import { apiPost, errorMessage } from '@/lib/api'
import { ROTAS_DE_ALOCACOES } from '@/lib/endpoints'
import { SEM_VALOR } from '@/lib/datas'
import { usePermissao } from '@/lib/permissoes'
import { cn } from '@/lib/utils'

/**
 * Troca de veículo no MEIO da locação (Adendo C).
 *
 * ---------------------------------------------------------------------------
 * UMA OPERAÇÃO, DUAS VISTORIAS — NUNCA MENOS
 * ---------------------------------------------------------------------------
 * Quebra, revisão dos 10 mil ou upgrade numa locação longa: o trecho atual
 * encerra com a vistoria de RETORNO do carro que sai e o novo abre com a
 * vistoria de SAÍDA do que entra — as duas no MESMO `POST`, porque uma troca
 * sem as duas pontas vistoriadas é exatamente o buraco de prova que a fase
 * existe para fechar. Por isso a tela é um fluxo em passos que só chega ao
 * confirmar com os dois corpos já validados pelo `vistoriaAvulsaSchema` (o
 * mesmo formulário da vistoria normal, reutilizado duas vezes).
 *
 * ---------------------------------------------------------------------------
 * QUEM ESCOLHE O CARRO NOVO É O ALGORITMO — O MANUAL SE JUSTIFICA
 * ---------------------------------------------------------------------------
 * O padrão é sem `vehicleId`: o scoring determinístico escolhe NA CONFIRMAÇÃO,
 * com a agenda daquele momento (a tela mostra a escolha ATUAL como prévia, e
 * diz que é prévia). Escolher à mão exige justificativa — regra do
 * `trocarVeiculoSchema`, e é o que a auditoria lê quando o carro escolhido à
 * mão der problema. Elegibilidade é dura: candidato bloqueado nem se seleciona.
 *
 * Os PASSOS FICAM MONTADOS (só escondidos) de propósito: a assinatura vive num
 * canvas, e desmontar o passo ao avançar apagaria a rubrica de quem voltasse
 * um passo para conferir uma foto.
 *
 * O 409 (agenda tomada entre a prévia e o confirmar) e o 422 (status da
 * reserva) chegam com a mensagem do envelope da API — a tela os exibe sem
 * traduzir de novo.
 */

const PASSOS = [
  'Motivo',
  'Devolução do atual',
  'Carro novo',
  'Saída do novo',
  'Confirmação',
] as const

/** Espelho do `alocacaoFeitaSchema` da API — só os campos que a tela usa. */
const trocaFeitaSchema = z.object({
  alocacaoId: z.uuid(),
  vehicleId: z.uuid(),
  placa: z.string(),
  modelo: z.string(),
  automatica: z.boolean(),
  score: z.number().int().nullable(),
})

const NUMERO_PT_BR = new Intl.NumberFormat('pt-BR')

/** "82.410 km · 3/4 · 9 fotos · 2 avarias" — o resumo de um corpo validado. */
function resumoDaVistoria(corpo: VistoriaAvulsa): string {
  return [
    `${NUMERO_PT_BR.format(corpo.hodometro)} km`,
    rotuloDoNivelDeCombustivel(corpo.combustivelEmOitavos),
    `${String(corpo.fotos.length)} ${corpo.fotos.length === 1 ? 'foto' : 'fotos'}`,
    `${String(corpo.avarias.length)} ${corpo.avarias.length === 1 ? 'avaria' : 'avarias'}`,
  ].join(' · ')
}

interface LinhaDeCandidatoProps {
  readonly candidato: CandidatoLido
  readonly selecionavel: boolean
  readonly selecionado: boolean
  readonly desabilitado: boolean
  readonly aoSelecionar: () => void
}

function LinhaDeCandidato({
  candidato,
  selecionavel,
  selecionado,
  desabilitado,
  aoSelecionar,
}: LinhaDeCandidatoProps): ReactElement {
  const conteudo = (
    <div className="min-w-0 flex-1 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-foreground">{candidato.placa}</span>
        <span className="min-w-0 text-sm text-fg-secondary">{candidato.modelo}</span>
        {candidato.elegivel ? (
          <Badge variant="success">Elegível</Badge>
        ) : (
          <Badge variant="danger">Bloqueado</Badge>
        )}
        <span className="text-xs tabular-money text-fg-secondary">
          score {String(candidato.score)}
        </span>
      </div>
      {candidato.bloqueios.length === 0 ? null : (
        <ul className="space-y-0.5">
          {candidato.bloqueios.map((bloqueio) => (
            <li key={bloqueio.motivo} className="text-xs text-fg-muted">
              {bloqueio.detalhe}
            </li>
          ))}
        </ul>
      )}
    </div>
  )

  if (!selecionavel) {
    return (
      <li className="flex items-start gap-3 rounded-lg border border-border bg-surface p-3">
        {conteudo}
      </li>
    )
  }

  return (
    <li>
      <label
        className={cn(
          'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
          selecionado ? 'border-border-strong bg-elevated' : 'border-border bg-surface',
          (!candidato.elegivel || desabilitado) && 'cursor-not-allowed opacity-60',
        )}
      >
        <input
          type="radio"
          name="carro-novo"
          className="mt-1 size-4 shrink-0 cursor-pointer accent-[var(--color-primary)]
            disabled:cursor-not-allowed"
          checked={selecionado}
          disabled={!candidato.elegivel || desabilitado}
          onChange={aoSelecionar}
          aria-label={`Escolher ${candidato.placa} — ${candidato.modelo}`}
        />
        {conteudo}
      </label>
    </li>
  )
}

export function TrocaDeVeiculoPage(): ReactElement {
  const podeTrocar = usePermissao('allocation.update')
  const reservaId = useParams({ strict: false }).reservaId ?? null
  const navegar = useNavigate()
  const queryClient = useQueryClient()

  const reserva = useReserva(reservaId)
  const candidatos = useCandidatos(reservaId)

  const [passo, setPasso] = useState(0)

  // Passo 1 — motivo.
  const [motivo, setMotivo] = useState('')
  const [erroDoMotivo, setErroDoMotivo] = useState<string | null>(null)

  // Passo 2 — devolução do carro atual.
  const [devolucao, setDevolucao] = useState<ValorDaVistoria>(VALOR_INICIAL_DA_VISTORIA)
  const [errosDaDevolucao, setErrosDaDevolucao] = useState<ErrosDaVistoria>({})
  const [corpoDaDevolucao, setCorpoDaDevolucao] = useState<VistoriaAvulsa | null>(null)
  const refDaDevolucao = useRef<FormularioDeVistoriaHandle | null>(null)

  // Passo 3 — o carro que entra.
  const [modoDaEscolha, setModoDaEscolha] = useState<'ALGORITMO' | 'MANUAL'>('ALGORITMO')
  const [vehicleId, setVehicleId] = useState<string | null>(null)
  const [justificativa, setJustificativa] = useState('')
  const [erroDaEscolha, setErroDaEscolha] = useState<string | null>(null)

  // Passo 4 — saída do carro novo.
  const [saida, setSaida] = useState<ValorDaVistoria>(VALOR_INICIAL_DA_VISTORIA)
  const [errosDaSaida, setErrosDaSaida] = useState<ErrosDaVistoria>({})
  const [corpoDaSaida, setCorpoDaSaida] = useState<VistoriaAvulsa | null>(null)
  const refDaSaida = useRef<FormularioDeVistoriaHandle | null>(null)

  const [preparando, setPreparando] = useState(false)
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const trocar = useMutation({
    mutationFn: (corpo: TrocarVeiculo) =>
      apiPost(ROTAS_DE_ALOCACOES.trocar(reservaId ?? ''), trocaFeitaSchema, corpo),
    onSuccess: () => {
      // A troca mexe em reserva, vistorias, agenda e frota — chaves que moram
      // em features paralelas. Gesto raro paga o cache inteiro, como no
      // registrar vistoria.
      void queryClient.invalidateQueries()
    },
  })

  const ocupado = preparando || trocar.isPending

  const placaQueSai = reserva.data?.veiculoAlocado?.placa ?? null
  const candidatoManual =
    candidatos.data?.candidatos.find((candidato) => candidato.vehicleId === vehicleId) ?? null
  const escolhaDoAlgoritmo =
    candidatos.data?.candidatos.find((candidato) => candidato.elegivel) ?? null

  function avancarDoMotivo(): void {
    const validado = motivoDeTransicaoSchema.safeParse(motivo)

    if (!validado.success) {
      setErroDoMotivo(validado.error.issues[0]?.message ?? 'motivo inválido')
      return
    }

    setErroDoMotivo(null)
    setPasso(1)
  }

  /**
   * O portão dos passos de vistoria: sobe a assinatura (cacheada até o traço
   * mudar), monta o corpo e o valida com o MESMO `vistoriaAvulsaSchema` que a
   * API executa dentro do `trocarVeiculoSchema` — avançar com vistoria capenga
   * só adiaria o erro para o pior momento, o confirmar.
   */
  async function avancarDaVistoria(qual: 'DEVOLUCAO' | 'SAIDA'): Promise<void> {
    const referencia = qual === 'DEVOLUCAO' ? refDaDevolucao : refDaSaida
    const valor = qual === 'DEVOLUCAO' ? devolucao : saida
    const definirErros = qual === 'DEVOLUCAO' ? setErrosDaDevolucao : setErrosDaSaida

    definirErros({})

    let chave: string | null = null
    setPreparando(true)

    try {
      chave = (await referencia.current?.obterAssinaturaChave()) ?? null
    } catch (causa) {
      toast.error('Não foi possível enviar a assinatura', { description: errorMessage(causa) })
      return
    } finally {
      setPreparando(false)
    }

    if (chave === null) {
      definirErros({
        assinaturaArquivoChave:
          'a assinatura do cliente é obrigatória — vistoria sem assinatura não prova concordância',
      })
      return
    }

    const validado = vistoriaAvulsaSchema.safeParse(corpoDaVistoria(valor, chave))

    if (!validado.success) {
      const { porCampo, sobras } = distribuirErrosDaVistoria(validado.error.issues)
      definirErros(porCampo)

      if (sobras.length > 0) {
        toast.error('Vistoria incompleta', { description: sobras.join(' · ') })
      }
      return
    }

    if (qual === 'DEVOLUCAO') {
      setCorpoDaDevolucao(validado.data)
      setPasso(2)
    } else {
      setCorpoDaSaida(validado.data)
      setPasso(4)
    }
  }

  function avancarDaEscolha(): void {
    if (modoDaEscolha === 'MANUAL') {
      if (vehicleId === null) {
        setErroDaEscolha('escolha o veículo que entra — ou deixe o algoritmo decidir')
        return
      }

      if (justificativa.trim().length < 5) {
        setErroDaEscolha(
          'escolher o veículo à mão exige justificativa — o algoritmo tinha outro candidato',
        )
        return
      }
    }

    setErroDaEscolha(null)
    setPasso(3)
  }

  function confirmar(): void {
    if (reservaId === null || corpoDaDevolucao === null || corpoDaSaida === null) {
      setErroGeral('Refaça os passos de vistoria — os dados de um deles se perderam.')
      return
    }

    setErroGeral(null)

    const corpo = {
      motivo: motivo.trim(),
      ...(modoDaEscolha === 'MANUAL' && vehicleId !== null
        ? { vehicleId, justificativa: justificativa.trim() }
        : {}),
      devolucaoDoTrecho: corpoDaDevolucao,
      saidaDoNovoTrecho: corpoDaSaida,
    }

    const validado = trocarVeiculoSchema.safeParse(corpo)

    if (!validado.success) {
      setErroGeral(validado.error.issues.map((issue) => issue.message).join(' · '))
      return
    }

    trocar.mutate(validado.data, {
      onSuccess: (feita) => {
        toast.success('Veículo trocado', {
          description: `${feita.placa} · ${feita.modelo} assume a locação — ${
            feita.automatica ? 'escolha do algoritmo' : 'escolha manual justificada'
          }. O fechamento somará os dois trechos.`,
        })
        void navegar({ to: '/reservas/$reservaId', params: { reservaId } })
      },
      onError: (causa: unknown) => {
        // 409 (a agenda do carro foi tomada entre a prévia e o confirmar) e
        // 422 (status da reserva, vistoria recusada) — a mensagem é a do
        // envelope da API.
        const mensagem = errorMessage(causa)
        setErroGeral(mensagem)
        toast.error('Não foi possível trocar o veículo', { description: mensagem })
      },
    })
  }

  if (reservaId === null) {
    return <NotFoundState />
  }

  const cabecalho = (
    <div className="flex flex-col gap-4">
      <Link
        to="/reservas/$reservaId"
        params={{ reservaId }}
        className="inline-flex w-fit items-center gap-1.5 text-sm text-fg-secondary
          hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Detalhe da reserva
      </Link>

      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Trocar o veículo da locação</h1>
        <p className="max-w-prose text-sm text-fg-secondary">
          O trecho atual encerra com a vistoria de devolução e o carro novo abre um trecho com a
          vistoria de saída — tudo numa operação só. O fechamento somará os trechos.
        </p>
      </div>
    </div>
  )

  if (!podeTrocar) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
        {cabecalho}
        <Alert variant="neutral">
          <AlertDescription>
            Trocar o veículo exige a permissão de alocação (allocation.update).
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (reserva.isPending) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
        {cabecalho}
        <LoadingState linhas={3} rotulo="Carregando a reserva…" />
      </div>
    )
  }

  if (reserva.isError) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
        {cabecalho}
        <ErrorState
          titulo="Não foi possível carregar a reserva"
          erro={reserva.error}
          aoTentarNovamente={() => {
            void reserva.refetch()
          }}
        />
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
      {cabecalho}

      {reserva.data.status === 'EM_ANDAMENTO' ? null : (
        <Alert variant="warning">
          <AlertTitle>A reserva não está em andamento</AlertTitle>
          <AlertDescription>
            A troca só vale com a locação EM ANDAMENTO — o status atual é{' '}
            {ROTULO_DO_STATUS_DE_RESERVA[reserva.data.status].rotulo}, e o servidor recusará a
            operação.
          </AlertDescription>
        </Alert>
      )}

      {/* O mapa do fluxo — cor E rótulo em cada passo, nunca só a bolinha. */}
      <ol className="flex flex-wrap items-center gap-x-3 gap-y-2" aria-label="Passos da troca">
        {PASSOS.map((rotulo, indice) => (
          <li
            key={rotulo}
            className="flex items-center gap-1.5"
            aria-current={indice === passo ? 'step' : undefined}
          >
            <span
              className={cn(
                'flex size-6 items-center justify-center rounded-full border text-xs font-semibold',
                indice < passo
                  ? 'border-success-fg/40 bg-success/10 text-success-fg'
                  : indice === passo
                    ? 'border-border-strong bg-elevated text-foreground'
                    : 'border-border bg-surface text-fg-muted',
              )}
            >
              {indice < passo ? (
                <Check className="size-3.5" aria-hidden="true" />
              ) : (
                String(indice + 1)
              )}
            </span>
            <span
              className={cn(
                'text-xs',
                indice === passo ? 'font-medium text-foreground' : 'text-fg-muted',
              )}
            >
              {rotulo}
            </span>
          </li>
        ))}
      </ol>

      {/* ------------------------------------------------------------
          Passo 1 — motivo
          ------------------------------------------------------------ */}
      <Card className={cn(passo === 0 ? undefined : 'hidden')}>
        <CardHeader>
          <CardTitle>Por que trocar?</CardTitle>
          <CardDescription>
            O motivo vai para a trilha da reserva — é ele que explica, meses depois, por que esta
            locação tem dois trechos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Campo
            rotulo="Motivo da troca"
            obrigatorio
            erro={erroDoMotivo ?? undefined}
            dica="Mínimo de 5 caracteres. Ex.: pane elétrica na retirada da Faria Lima; revisão dos 10 mil venceu no meio da locação."
          >
            {(campo) => (
              <Textarea
                {...campo}
                value={motivo}
                onChange={(evento) => {
                  setMotivo(evento.target.value)
                }}
                disabled={ocupado}
              />
            )}
          </Campo>

          <div className="flex justify-end">
            <Button type="button" onClick={avancarDoMotivo} disabled={ocupado}>
              Avançar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------
          Passo 2 — devolução do carro atual
          ------------------------------------------------------------ */}
      <Card className={cn(passo === 1 ? undefined : 'hidden')}>
        <CardContent className="space-y-6 pt-6">
          <FormularioDeVistoria
            ref={refDaDevolucao}
            titulo={
              placaQueSai === null
                ? 'Vistoria de devolução do carro atual'
                : `Vistoria de devolução — ${placaQueSai}`
            }
            descricao="O estado em que o carro que SAI volta para o pátio. É contra esta vistoria que o comparativo do trecho fecha."
            valor={devolucao}
            aoMudar={setDevolucao}
            erros={errosDaDevolucao}
            desabilitado={ocupado}
          />

          <div className="flex justify-between gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setPasso(0)
              }}
              disabled={ocupado}
            >
              Voltar
            </Button>
            <Button
              type="button"
              onClick={() => {
                void avancarDaVistoria('DEVOLUCAO')
              }}
              disabled={ocupado || devolucao.enviandoAngulos.length > 0}
            >
              {preparando ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Avançar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------
          Passo 3 — o carro que entra
          ------------------------------------------------------------ */}
      <Card className={cn(passo === 2 ? undefined : 'hidden')}>
        <CardHeader>
          <CardTitle>Escolha do carro novo</CardTitle>
          <CardDescription>
            O algoritmo pontua os candidatos da categoria com os pesos da casa; a escolha manual
            exige justificativa. Candidato bloqueado não entra — elegibilidade é dura, peso nenhum a
            compra.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            role="group"
            aria-label="Como escolher o carro novo"
            className="grid gap-1 rounded-lg border border-border bg-surface p-1 sm:grid-cols-2"
          >
            <button
              type="button"
              aria-pressed={modoDaEscolha === 'ALGORITMO'}
              onClick={() => {
                setModoDaEscolha('ALGORITMO')
                setErroDaEscolha(null)
              }}
              disabled={ocupado}
              className={cn(
                'rounded-md px-3 py-2 text-sm font-medium transition-colors',
                modoDaEscolha === 'ALGORITMO'
                  ? 'bg-elevated text-foreground'
                  : 'text-fg-secondary hover:text-foreground',
              )}
            >
              O algoritmo escolhe (padrão)
            </button>
            <button
              type="button"
              aria-pressed={modoDaEscolha === 'MANUAL'}
              onClick={() => {
                setModoDaEscolha('MANUAL')
                setErroDaEscolha(null)
              }}
              disabled={ocupado}
              className={cn(
                'rounded-md px-3 py-2 text-sm font-medium transition-colors',
                modoDaEscolha === 'MANUAL'
                  ? 'bg-elevated text-foreground'
                  : 'text-fg-secondary hover:text-foreground',
              )}
            >
              Escolher manualmente
            </button>
          </div>

          {candidatos.isPending ? (
            <LoadingState linhas={3} rotulo="Pontuando os candidatos…" />
          ) : candidatos.isError ? (
            <ErrorState
              titulo="Não foi possível pontuar os candidatos"
              erro={candidatos.error}
              aoTentarNovamente={() => {
                void candidatos.refetch()
              }}
            />
          ) : (
            <>
              {modoDaEscolha === 'ALGORITMO' ? (
                escolhaDoAlgoritmo === null ? (
                  <Alert variant="warning">
                    <AlertTitle>Nenhum candidato elegível agora</AlertTitle>
                    <AlertDescription>
                      Os bloqueios de cada carro estão listados abaixo. O servidor recusará a troca
                      enquanto nenhum candidato estiver elegível.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="info">
                    <AlertTitle>
                      Prévia do algoritmo: {escolhaDoAlgoritmo.placa} · {escolhaDoAlgoritmo.modelo}{' '}
                      (score {String(escolhaDoAlgoritmo.score)})
                    </AlertTitle>
                    <AlertDescription>
                      A escolha definitiva acontece na confirmação, com a agenda daquele momento —
                      se este carro for tomado no meio do caminho, o próximo elegível assume.
                    </AlertDescription>
                  </Alert>
                )
              ) : null}

              {candidatos.data.candidatos.length === 0 ? (
                <p className="text-sm text-fg-muted">
                  Nenhum veículo da categoria para pontuar — confira a frota.
                </p>
              ) : (
                <ul className="space-y-2">
                  {candidatos.data.candidatos.map((candidato) => (
                    <LinhaDeCandidato
                      key={candidato.vehicleId}
                      candidato={candidato}
                      selecionavel={modoDaEscolha === 'MANUAL'}
                      selecionado={vehicleId === candidato.vehicleId}
                      desabilitado={ocupado}
                      aoSelecionar={() => {
                        setVehicleId(candidato.vehicleId)
                      }}
                    />
                  ))}
                </ul>
              )}
            </>
          )}

          {modoDaEscolha === 'MANUAL' ? (
            <Campo
              rotulo="Justificativa da escolha manual"
              obrigatorio
              dica="O algoritmo tinha outro candidato — diga por que este. É o que a auditoria lê."
            >
              {(campo) => (
                <Textarea
                  {...campo}
                  value={justificativa}
                  onChange={(evento) => {
                    setJustificativa(evento.target.value)
                  }}
                  disabled={ocupado}
                />
              )}
            </Campo>
          ) : null}

          {erroDaEscolha === null ? null : (
            <p className="text-xs text-danger-fg">{erroDaEscolha}</p>
          )}

          <div className="flex justify-between gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setPasso(1)
              }}
              disabled={ocupado}
            >
              Voltar
            </Button>
            <Button type="button" onClick={avancarDaEscolha} disabled={ocupado}>
              Avançar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------
          Passo 4 — saída do carro novo
          ------------------------------------------------------------ */}
      <Card className={cn(passo === 3 ? undefined : 'hidden')}>
        <CardContent className="space-y-6 pt-6">
          <FormularioDeVistoria
            ref={refDaSaida}
            titulo={
              modoDaEscolha === 'MANUAL' && candidatoManual !== null
                ? `Vistoria de saída — ${candidatoManual.placa}`
                : 'Vistoria de saída do carro novo'
            }
            descricao={
              modoDaEscolha === 'MANUAL'
                ? 'O estado em que o carro escolhido ENTRA na locação.'
                : 'O estado em que o carro que o algoritmo escolher ENTRA na locação — vistorie o carro que o pátio separou.'
            }
            valor={saida}
            aoMudar={setSaida}
            erros={errosDaSaida}
            desabilitado={ocupado}
          />

          <div className="flex justify-between gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setPasso(2)
              }}
              disabled={ocupado}
            >
              Voltar
            </Button>
            <Button
              type="button"
              onClick={() => {
                void avancarDaVistoria('SAIDA')
              }}
              disabled={ocupado || saida.enviandoAngulos.length > 0}
            >
              {preparando ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Avançar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------
          Passo 5 — resumo e confirmação
          ------------------------------------------------------------ */}
      <Card className={cn(passo === 4 ? undefined : 'hidden')}>
        <CardHeader>
          <CardTitle>Confirmar a troca</CardTitle>
          <CardDescription>
            Confira antes de confirmar — a operação encerra o trecho atual AGORA e é o servidor quem
            valida agenda, status e as duas vistorias, de uma vez.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <dl className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-fg-muted">Motivo</dt>
              <dd className="text-sm text-foreground">
                {motivo.trim() === '' ? SEM_VALOR : motivo}
              </dd>
            </div>

            <div className="space-y-1">
              <dt className="text-xs font-medium uppercase tracking-wide text-fg-muted">
                Carro que sai
              </dt>
              <dd className="text-sm text-foreground">
                {placaQueSai ?? SEM_VALOR}
                {reserva.data.veiculoAlocado === null
                  ? ''
                  : ` · ${reserva.data.veiculoAlocado.modelo}`}
              </dd>
              <dd className="text-xs tabular-money text-fg-secondary">
                Devolução:{' '}
                {corpoDaDevolucao === null
                  ? 'vistoria pendente'
                  : resumoDaVistoria(corpoDaDevolucao)}
              </dd>
            </div>

            <div className="space-y-1">
              <dt className="text-xs font-medium uppercase tracking-wide text-fg-muted">
                Carro que entra
              </dt>
              <dd className="text-sm text-foreground">
                {modoDaEscolha === 'MANUAL' && candidatoManual !== null
                  ? `${candidatoManual.placa} · ${candidatoManual.modelo} — escolha manual`
                  : 'Escolha do algoritmo na confirmação'}
              </dd>
              {modoDaEscolha === 'MANUAL' ? (
                <dd className="text-xs text-fg-secondary">Justificativa: {justificativa}</dd>
              ) : escolhaDoAlgoritmo === null ? null : (
                <dd className="text-xs text-fg-secondary">
                  Prévia: {escolhaDoAlgoritmo.placa} · {escolhaDoAlgoritmo.modelo} (score{' '}
                  {String(escolhaDoAlgoritmo.score)})
                </dd>
              )}
              <dd className="text-xs tabular-money text-fg-secondary">
                Saída:{' '}
                {corpoDaSaida === null ? 'vistoria pendente' : resumoDaVistoria(corpoDaSaida)}
              </dd>
            </div>
          </dl>

          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-between gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setPasso(3)
              }}
              disabled={ocupado}
            >
              Voltar
            </Button>
            <Button type="button" onClick={confirmar} disabled={ocupado}>
              {trocar.isPending ? (
                <LoaderCircle className="animate-spin" aria-hidden="true" />
              ) : (
                <ArrowRightLeft aria-hidden="true" />
              )}
              Confirmar troca
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
